import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, Pencil, DoorOpen } from 'lucide-react'
import type { PageProps } from '@/types'

interface Space {
  id: number
  name: string
  kind: string
  color: string | null
  active: boolean
}

interface SpacesIndexProps extends PageProps {
  spaces: Space[]
  can_manage?: boolean
}

const DEFAULT_COLOR = '#0f6c5b'

function csrfToken(): string {
  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  )
}

export default function SpacesIndex({
  spaces,
  can_manage = true,
  current_user,
  current_organization,
  current_business,
  flash,
}: SpacesIndexProps) {
  const orgSlug = current_organization?.slug || ''
  const editParam =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('edit')
      : null
  const editId = editParam ? Number(editParam) : NaN
  const initialEdit =
    Number.isFinite(editId) ? spaces.find((space) => space.id === editId) ?? null : null
  const [editing, setEditing] = useState<Space | null>(initialEdit)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState(initialEdit?.name ?? '')
  const [color, setColor] = useState(initialEdit?.color || DEFAULT_COLOR)
  const [active, setActive] = useState(initialEdit?.active ?? true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startCreate = () => {
    setEditing(null)
    setCreating(true)
    setName('')
    setColor(DEFAULT_COLOR)
    setActive(true)
    setError(null)
  }

  const startEdit = (space: Space) => {
    setCreating(false)
    setEditing(space)
    setName(space.name)
    setColor(space.color || DEFAULT_COLOR)
    setActive(space.active)
    setError(null)
  }

  const cancel = () => {
    setCreating(false)
    setEditing(null)
    setError(null)
  }

  const save = async () => {
    if (!name.trim()) {
      setError('El nombre es obligatorio')
      return
    }
    setSaving(true)
    setError(null)

    const url = editing
      ? `/${orgSlug}/spaces/${editing.id}`
      : `/${orgSlug}/spaces`

    try {
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ space: { name: name.trim(), color, active } }),
      })
      if (!response.ok) {
        setError('No se pudo guardar el espacio')
        setSaving(false)
        return
      }
      cancel()
      router.reload({ only: ['spaces', 'has_spaces'] })
    } catch {
      setError('No se pudo guardar el espacio')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (space: Space) => {
    if (!confirm(`¿Eliminar el espacio "${space.name}"?`)) return
    await fetch(`/${orgSlug}/spaces/${space.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken() },
    })
    router.reload({ only: ['spaces', 'has_spaces'] })
  }

  const showForm = creating || editing !== null

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Espacios</h1>
            <p className="text-muted-foreground">
              Salas, cabinas y equipos donde se realizan tus servicios
            </p>
          </div>
          {can_manage && !showForm && (
            <Button className="w-full sm:w-auto" onClick={startCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo espacio
            </Button>
          )}
        </div>

        {flash?.notice && (
          <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
            {flash.notice}
          </div>
        )}

        {showForm && (
          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">
                Nombre
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sala 4 – Láser"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-muted-foreground">
                  Color
                </label>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-16 rounded border border-border bg-background"
                />
              </div>
              <label className="flex items-center gap-2 pt-5 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                Activo
              </label>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
              <Button variant="ghost" onClick={cancel} disabled={saving}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {spaces.length === 0 && !showForm ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center">
            <DoorOpen className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">
              Aún no tienes espacios. Crea salas o equipos para que el sistema
              evite empalmes en la misma máquina.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card">
            <div className="divide-y divide-border">
              {spaces.map((space) => (
                <div
                  key={space.id}
                  className="flex items-center justify-between gap-4 p-4"
                >
                  <Link
                    href={`/${orgSlug}/spaces/${space.id}`}
                    className="flex min-w-0 items-center gap-3 rounded-lg hover:bg-accent/50"
                  >
                    <span
                      className="h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: space.color || DEFAULT_COLOR }}
                    />
                    <span className="truncate font-medium text-card-foreground">
                      {space.name}
                    </span>
                    {!space.active && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        Inactivo
                      </span>
                    )}
                  </Link>
                  {can_manage && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(space)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(space)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
